import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatEUR } from '@/lib/utils';

export interface WaterfallStep {
  /** Libellé affiché à gauche de la barre. */
  label: string;
  /** Montant du poste (négatif = remise / gain). */
  value: number;
}

interface PriceWaterfallProps {
  /** Prix de base (première barre, ancre de départ). */
  base: number;
  /** Postes additionnels dans l'ordre d'application. */
  steps: WaterfallStep[];
  /** Total facturé (dernière barre, ancre d'arrivée). */
  total: number;
  totalLabel: string;
  baseLabel: string;
}

const ROW_H = 26;
const GAP = 6;
const LABEL_W = 116;
const VALUE_W = 84;
const RADIUS = 4;
/** Épaisseur minimale d'une barre : un poste non nul reste visible. */
const MIN_BAR = 3;

type Kind = 'anchor' | 'increase' | 'decrease';

interface Bar {
  label: string;
  value: number;
  kind: Kind;
  /** Position de départ sur l'axe des montants. */
  from: number;
  to: number;
}

/**
 * Cascade de décomposition du prix : prix de base → surcharges → gazole →
 * coûts de site → prestations, jusqu'au total facturé. Chaque poste montre
 * sa contribution réelle au prix, ce qu'une liste de montants ne donne pas.
 *
 * Rendu en SVG sans dépendance (le reste de l'app fait de même). Les couleurs
 * viennent de tokens validés pour les deux thèmes ; la lecture ne repose
 * jamais sur la seule couleur : chaque barre porte son montant en clair et le
 * détail chiffré complet suit dans le tiroir.
 */
export function PriceWaterfall({
  base,
  steps,
  total,
  totalLabel,
  baseLabel,
}: PriceWaterfallProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const [hovered, setHovered] = useState<number | null>(null);

  const meaningful = steps.filter((s) => Math.abs(s.value) > 0.005);

  const bars: Bar[] = [];
  bars.push({ label: baseLabel, value: base, kind: 'anchor', from: 0, to: base });
  let running = base;
  for (const s of meaningful) {
    const from = running;
    running = Math.round((running + s.value) * 100) / 100;
    bars.push({
      label: s.label,
      value: s.value,
      kind: s.value < 0 ? 'decrease' : 'increase',
      from: Math.min(from, running),
      to: Math.max(from, running),
    });
  }
  bars.push({ label: totalLabel, value: total, kind: 'anchor', from: 0, to: total });

  // Une seule barre utile (aucun poste) : la cascade n'apprend rien.
  if (meaningful.length === 0) return null;

  const max = Math.max(...bars.map((b) => b.to), total, base) || 1;
  const plotW = 360;
  const height = bars.length * ROW_H + GAP;
  const scale = (v: number) => (v / max) * plotW;
  const hasDecrease = bars.some((b) => b.kind === 'decrease');

  return (
    <figure className="m-0">
      <svg
        role="img"
        aria-labelledby={titleId}
        viewBox={`0 0 ${LABEL_W + plotW + VALUE_W} ${height}`}
        className="w-full"
        style={{ maxHeight: height * 1.6 }}
      >
        <title id={titleId}>
          {t('breakdown.waterfallTitle', { total: formatEUR(total) })}
        </title>
        {bars.map((b, i) => {
          const y = i * ROW_H;
          const x = LABEL_W + scale(b.from);
          const w = Math.max(scale(b.to - b.from), MIN_BAR);
          const fill =
            b.kind === 'anchor'
              ? 'var(--viz-anchor)'
              : b.kind === 'decrease'
                ? 'var(--viz-decrease)'
                : 'var(--viz-increase)';
          const isLast = i === bars.length - 1;
          return (
            <g
              key={`${b.label}-${i}`}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Cible de survol plus large que la barre. */}
              <rect
                x={0}
                y={y}
                width={LABEL_W + plotW + VALUE_W}
                height={ROW_H}
                fill={hovered === i ? 'var(--surface-subtle)' : 'transparent'}
              />
              {/* Trait de report vers le poste suivant. */}
              {!isLast && i < bars.length - 2 && (
                <line
                  x1={LABEL_W + scale(bars[i].kind === 'anchor' ? bars[i].to : bars[i].to)}
                  y1={y + ROW_H - 5}
                  x2={LABEL_W + scale(bars[i].kind === 'anchor' ? bars[i].to : bars[i].to)}
                  y2={y + ROW_H + 3}
                  stroke="var(--border)"
                  strokeWidth={1}
                />
              )}
              <text
                x={LABEL_W - 8}
                y={y + ROW_H / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-[color:var(--text-secondary)] text-[10px]"
              >
                {b.label}
              </text>
              <rect
                x={x}
                y={y + 5}
                width={w}
                height={ROW_H - 12}
                rx={RADIUS}
                fill={fill}
                opacity={hovered == null || hovered === i ? 1 : 0.55}
              />
              <text
                x={LABEL_W + plotW + 8}
                y={y + ROW_H / 2}
                dominantBaseline="middle"
                className="fill-[color:var(--text-primary)] text-[10px] tabular-nums"
                style={{ fontWeight: b.kind === 'anchor' ? 600 : 400 }}
              >
                {b.kind === 'increase' && '+'}
                {formatEUR(b.value)}
              </text>
            </g>
          );
        })}
      </svg>
      {hasDecrease && (
        <figcaption className="mt-1 flex items-center gap-3 text-2xs text-subtle">
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: 'var(--viz-increase)' }}
              aria-hidden="true"
            />
            {t('breakdown.waterfallIncrease')}
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: 'var(--viz-decrease)' }}
              aria-hidden="true"
            />
            {t('breakdown.waterfallDecrease')}
          </span>
        </figcaption>
      )}
    </figure>
  );
}
